<template>
  <main class="wallet-transfer-page">
    <AppNavBar :title="t('home.transfer')" />

    <section class="wallet-card">
      <h2>{{ t('wallet.receiveAddress') }}</h2>
      <van-field
        v-model="addressTo"
        clearable
        :placeholder="t('wallet.enterOrPasteAddress')"
      >
        <template #button>
          <van-icon name="scan" @click="openScanner" />
        </template>
      </van-field>
    </section>

    <section class="wallet-card selector-card" @click="showCoinPicker = true">
      <div class="coin-title">
        <app-image :src="selectedCoin.img || selectedCoin.logo || usdtIcon" alt="" />
        <div data-strong>{{ selectedCoin.symbol || selectedCoin.coin || '--' }}</div>
      </div>
      <span>{{ t('wallet.chooseCoin') }} <van-icon name="arrow" /></span>
    </section>

    <section class="wallet-card">
      <div class="amount-title">
        <h2>{{ t('wallet.transferAmount') }}</h2>
        <span>{{ t('wallet.availableBalance') }} {{ availableText }}</span>
      </div>

      <div class="amount-field">
        <van-field
          v-model="amount"
          type="number"
          clearable
          input-align="left"
          :placeholder="t('wallet.enterAmount')"
        />
        <div class="amount-side">
          <span class="amount-symbol">{{ selectedCoin.symbol || selectedCoin.coin || '' }}</span>
          <button class="all-btn" type="button" @click="fillAll">{{ t('wallet.all') }}</button>
        </div>
      </div>

      <div class="wallet-balance-row">
        <span>{{ t('wallet.availableBalance') }}</span>
        <div data-strong>{{ availableText }}</div>
      </div>
    </section>

    <van-button block round type="primary" class="submit-btn" :disabled="submitDisabled" @click="submitTransfer">
      {{ t('common.confirm') }}
    </van-button>

    <van-popup v-model:show="showCoinPicker" round position="bottom">
      <section class="coin-picker">
        <header class="coin-picker__header">
          <h3>{{ t('wallet.chooseCoin') }}</h3>
          <van-icon name="cross" @click="showCoinPicker = false" />
        </header>

        <div class="coin-picker__list">
          <button
            v-for="item in coinList"
            :key="item.contractAddress || item.symbol || item.coin"
            type="button"
            class="coin-picker__item"
            :class="{ active: isSelectedCoin(item) }"
            @click="selectCoin(item)"
          >
            <div class="coin-picker__meta">
              <app-image :src="item.img || item.logo || usdtIcon" alt="" />
              <div>
                <div data-strong>{{ item.symbol || item.coin || '--' }}</div>
                <span>{{ formatTokenAmount(item.balance || 0) }}</span>
              </div>
            </div>
            <van-icon v-if="isSelectedCoin(item)" name="success" />
          </button>
        </div>
      </section>
    </van-popup>

    <PayPasswordPopup
      v-model:show="showPayPassword"
      v-model:password="payPassword"
      :title="t('auth.verifyWalletPassword')"
      :placeholder="t('auth.enterPassword')"
      :confirm-text="t('common.confirm')"
      :forgot-text="t('auth.forgotPasswordRecovery')"
      :show-forgot="true"
      :confirm-disabled="!payPassword.trim()"
      @confirm="confirmPassword"
      @cancel="resetPasswordPopup"
      @closed="resetPasswordPopup"
      @forgot="goForgotPassword"
    />
  </main>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { showFailToast, showToast } from 'vant';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import PayPasswordPopup from '@/components/PayPasswordPopup.vue';
import { payPwdCheck } from '@/api/auth';
import { useWalletScanner } from '@/composables/useWalletScanner';
import { useProfileStore } from '@/stores/profile';
import { useWalletTokenStore } from '@/stores/wallet-token';
import { formatTokenAmount } from '@/utils/format';
import { getRouteState, navigateWithState } from '@/utils/route-state';
import usdtIcon from '@/assets/images/usdt.png';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { openWalletScanner } = useWalletScanner();
const profileStore = useProfileStore();
const walletTokenStore = useWalletTokenStore();
const pageState = computed(() => getRouteState(route, 'wallet-transfer', {}));

const addressTo = ref('');
const amount = ref('');
const showCoinPicker = ref(false);
const showPayPassword = ref(false);
const payPassword = ref('');

const coinList = computed(() => walletTokenStore.assetList);
const selectedCoin = computed(() => (
  walletTokenStore.currentAsset
  || walletTokenStore.assetList[0]
  || {
    symbol: 'USDT',
    coin: 'USDT',
    balance: '0',
    img: '',
    logo: ''
  }
));
const availableText = computed(() => `${formatTokenAmount(selectedCoin.value.balance || 0)} ${selectedCoin.value.symbol || selectedCoin.value.coin || ''}`.trim());
const availableAmount = computed(() => Number(selectedCoin.value.balance || 0));
const amountValue = computed(() => Number(amount.value || 0));
const submitDisabled = computed(() => !addressTo.value.trim() || !amountValue.value);

watch(
  () => pageState.value.scanResult,
  (value) => {
    if (!value) {
      return;
    }
    addressTo.value = String(value).split('&')[0];
    navigateWithState(router, route.path, {
      namespace: 'wallet-transfer',
      replace: true,
      payload: {
        ...pageState.value,
        scanResult: ''
      }
    });
  },
  { immediate: true }
);

function isSelectedCoin(item) {
  const currentContract = String(selectedCoin.value.contractAddress || '').toLowerCase();
  const itemContract = String(item?.contractAddress || '').toLowerCase();
  if (currentContract || itemContract) {
    return currentContract === itemContract;
  }
  return String(selectedCoin.value.symbol || selectedCoin.value.coin || '') === String(item?.symbol || item?.coin || '');
}

function selectCoin(item) {
  walletTokenStore.setCurrentAsset(item);
  amount.value = '';
  showCoinPicker.value = false;
}

function openScanner() {
  openWalletScanner({
    back: '/wallet/transfer',
    stateNamespace: 'wallet-transfer',
    statePayload: {
      ...pageState.value
    }
  });
}

function fillAll() {
  amount.value = String(selectedCoin.value.balance || '0');
}

function submitTransfer() {
  if (!addressTo.value.trim()) {
    showFailToast(t('wallet.enterOrPasteAddress'));
    return;
  }

  if (!amount.value.trim()) {
    showFailToast(t('wallet.enterAmount'));
    return;
  }

  if (!amountValue.value || Number.isNaN(amountValue.value)) {
    showFailToast(t('card.enterValidAmount'));
    return;
  }

  if (amountValue.value <= 0) {
    showFailToast(t('card.enterValidAmount'));
    return;
  }

  if (amountValue.value > availableAmount.value) {
    showFailToast(`${t('wallet.maxAmountLabel')} ${availableText.value}`);
    return;
  }

  showPayPassword.value = true;
}

function resetPasswordPopup() {
  payPassword.value = '';
  showPayPassword.value = false;
}

async function confirmPassword() {
  const payPwd = String(payPassword.value || '').trim();
  if (!payPwd) {
    showFailToast(t('auth.enterPassword'));
    return;
  }

  const result = await payPwdCheck({ payPwd });
  if (!result) {
    showFailToast(t('auth.passwordError'));
    return;
  }

  resetPasswordPopup();
  showToast(t('wallet.signTodo'));
}

function goForgotPassword() {
  resetPasswordPopup();
  router.push('/auth/forgot-pay-password');
}

async function loadAssets() {
  await walletTokenStore.fetchAssetList(profileStore.platformAddress || '', { meta: { showLoading: false } });
  if (!walletTokenStore.currentAsset && walletTokenStore.assetList.length) {
    walletTokenStore.setCurrentAsset(walletTokenStore.assetList[0]);
  }
}

onMounted(loadAssets);
</script>

<style scoped lang="scss">
.wallet-transfer-page {
  min-height: var(--app-viewport-height);
  padding: 0 15px 34px;
  background: #f7f7f7;
}

.wallet-card {
  margin-top: 10px;
  padding: 13px 16px;
  border-radius: 12px;
  background: #ffffff;
}

 h2 {
  margin: 0 0 14px;
  color: #111111;
  font-size: 16px;
  font-weight: 500;
}

.selector-card,
.amount-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.selector-card > span,
.amount-title span {
  color: rgba(0, 0, 0, 0.65);
  font-size: 14px;
}

.coin-title {
  display: flex;
  align-items: center;
  gap: 10px;

  .app-image {
    width: 34px;
    height: 34px;
    border-radius: 17px;
    object-fit: cover;
  }

  [data-strong] {
    font-size: 16px;
    font-weight: 700;
    color: #111111;
  }
}

.amount-field {
  display: flex;
  align-items: center;
  gap: 12px;
}

.amount-field :deep(.van-field) {
  flex: 1;
  padding: 0;
}

.amount-side {
  display: flex;
  align-items: center;
  gap: 12px;
}

.amount-symbol {
  color: #111111;
  font-size: 16px;
  font-weight: 500;
}

.all-btn {
  border: 0;
  background: transparent;
  color: var(--van-primary-color);
  font-size: 14px;
}

.submit-btn {
  margin-top: 24px;
}

.wallet-balance-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px dashed #dfe3eb;

  span {
    color: #111111;
    font-size: 15px;
    font-weight: 500;
  }

  [data-strong] {
    color: #111111;
    font-size: 15px;
    font-weight: 500;
  }
}

.coin-picker {
  padding: 16px 16px 24px;
  background: #ffffff;
}

.coin-picker__header {
  display: flex;
  align-items: center;
  justify-content: space-between;

  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 500;
    color: #111111;
  }
}

.coin-picker__list {
  max-height: 55vh;
  margin-top: 16px;
  overflow-y: auto;
}

.coin-picker__item {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border: 0;
  border-bottom: 1px solid #f2f3f5;
  background: transparent;
  text-align: left;

  &:last-child {
    border-bottom: 0;
  }

  &.active {
    color: var(--van-primary-color);
  }
}

.coin-picker__meta {
  display: flex;
  align-items: center;
  gap: 10px;

  .app-image {
    width: 40px;
    height: 40px;
    border-radius: 20px;
    object-fit: cover;
  }

  [data-strong],
  span {
    display: block;
  }

  [data-strong] {
    color: #111111;
    font-size: 15px;
    font-weight: 500;
  }

  span {
    margin-top: 4px;
    color: #999999;
    font-size: 12px;
  }
}
</style>
